// Queries de REGISTRO_DIARIO.

import { apiFetch, qk, useApiMutation, useApiQuery } from './client';
import { currentOwner, getSnapshot, reportStorageError } from '@/lib/offline/cache';
import { getBundle } from '@/lib/offline/bundle';
import {
  enqueueParte,
  getParteSaveError,
  readProjectedParte,
  syncPendingParts,
} from '@/lib/offline/parts';
import type {
  ParteDelDiaPorCamada,
  ParteDelDiaUpsertInput,
  RegistroDiario,
  RegistroDiarioCreateInput,
} from '@/lib/types';

const REGISTROS_KEY = qk('registros-diarios');
const PARTES_CAMADA_KEY = qk('camadas');

/** Lista de partes de un lote. Si no se pasan filtros, todos. */
export function useRegistrosDeLote(
  loteId: number | null,
  fechaDesde?: string,
  fechaHasta?: string,
) {
  const params = new URLSearchParams();
  if (fechaDesde) params.set('fecha_desde', fechaDesde);
  if (fechaHasta) params.set('fecha_hasta', fechaHasta);
  const qs = params.toString();

  return useApiQuery<RegistroDiario[]>({
    queryKey:
      loteId != null
        ? [...REGISTROS_KEY, 'lote', loteId, qs]
        : qk('registros-diarios', 'lote', 'none'),
    queryFn: () =>
      apiFetch<RegistroDiario[]>(`/v1/lotes-nave/${loteId}/registros-diarios${qs ? `?${qs}` : ''}`),
    enabled: loteId != null,
    staleTime: 30 * 1000,
  });
}

/** Busca un parte concreto de un lote por fecha (YYYY-MM-DD). */
export function useParteDelDia(loteId: number | null, fecha: string | null) {
  const query = useRegistrosDeLote(loteId, fecha ?? undefined, fecha ?? undefined);
  const parte = query.data?.find((r) => r.fecha === fecha) ?? null;
  return { ...query, data: parte };
}

/** Argumento de useCrearRegistro: el lote viene en la URL, el body
 *  es lo que va en el payload. Separar evita meter lote_nave_id en
 *  el schema zod y luego tener que quitarlo. */
export interface CrearRegistroArgs {
  lote_nave_id: number;
  body: Omit<RegistroDiarioCreateInput, 'lote_nave_id'>;
}

/** Crea un parte en un lote. Devuelve el parte creado. */
export function useCrearRegistro() {
  return useApiMutation<CrearRegistroArgs, RegistroDiario>({
    mutationFn: ({ lote_nave_id, body }, context) =>
      apiFetch<RegistroDiario>(`/v1/lotes-nave/${lote_nave_id}/registros-diarios`, {
        method: 'POST',
        body: JSON.stringify(body),
        offlineBase: context.offlineBase,
      }),
    invalidateKeys: [['liquidacion-evolucion']],
  });
}

/** Edita un parte existente. */
export function useActualizarRegistro(loteId: number, regId: number) {
  return useApiMutation<Omit<RegistroDiarioCreateInput, 'lote_nave_id'>, RegistroDiario>({
    mutationFn: (body, context) =>
      apiFetch<RegistroDiario>(`/v1/lotes-nave/${loteId}/registros-diarios/${regId}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
        offlineBase: context.offlineBase,
      }),
    invalidateKeys: [['liquidacion-evolucion']],
  });
}

/** Parte diario del día de una camada con TODAS sus naves (modelo
 *  nuevo). Devuelve un ParteDelDiaPorCamada con N entradas (una por
 *  lote activo). Si una nave aún no tiene parte guardado, su entrada
 *  viene con ``id=null`` y los contadores a 0. */
export function useParteDelDiaPorCamada(camadaId: number | null, fecha: string | null) {
  const enabled = camadaId != null && fecha != null;
  return useApiQuery<ParteDelDiaPorCamada>({
    queryKey: enabled
      ? [...PARTES_CAMADA_KEY, camadaId, 'registros-diarios', fecha]
      : qk('camadas', 'none', 'registros-diarios', 'none'),
    queryFn: () =>
      apiFetch<ParteDelDiaPorCamada>(`/v1/camadas/${camadaId}/registros-diarios?fecha=${fecha}`),
    project: (baseline) => readProjectedParte(camadaId!, fecha!, baseline),
    enabled,
    staleTime: 30 * 1000,
  });
}

/** Upsert atómico del parte del día de una camada con N naves. Si el
 *  backend rechaza UNA sola nave, no se persiste NADA. */
export function useUpsertPartePorCamada(
  camadaId: number,
  displayedBaseline?: ParteDelDiaPorCamada,
) {
  return useApiMutation<ParteDelDiaUpsertInput, ParteDelDiaPorCamada>({
    mutationFn: async (body, context) => {
      const ownerId = currentOwner();
      if (await getBundle(ownerId)) {
        return apiFetch<ParteDelDiaPorCamada>(`/v1/camadas/${camadaId}/registros-diarios`, {
          method: 'POST',
          body: JSON.stringify(body),
          offlineBase: context.offlineBase,
          ...(ownerId == null ? {} : { expectedOwnerId: ownerId }),
        });
      }
      const onlineSave = () => {
        if (ownerId == null || currentOwner() !== ownerId)
          throw new Error('La sesión ha cambiado. Vuelve a guardar con tu usuario.');
        const payload = displayedBaseline?.revision
          ? {
              ...body,
              operation_uuid: crypto.randomUUID(),
              expected_revision: displayedBaseline.revision,
              expected_explotacion_id: displayedBaseline.explotacion_id,
            }
          : body;
        return apiFetch<ParteDelDiaPorCamada>(`/v1/camadas/${camadaId}/registros-diarios`, {
          method: 'POST',
          body: JSON.stringify(payload),
          expectedOwnerId: ownerId,
        });
      };
      const key = [...PARTES_CAMADA_KEY, camadaId, 'registros-diarios', body.fecha];
      let baseline: ParteDelDiaPorCamada | undefined = displayedBaseline;
      try {
        const cached = await getSnapshot<ParteDelDiaPorCamada>(key, ownerId);
        baseline ??= cached;
      } catch (error) {
        reportStorageError(error);
        // IndexedDB-disabled browsers retain the existing online workflow.
        // A failed offline write never produces a successful save response.
        if (navigator.onLine !== false) return onlineSave();
        throw new Error('Almacenamiento local no disponible. El parte no se ha guardado.');
      }
      if (!baseline)
        baseline = await apiFetch<ParteDelDiaPorCamada>(
          `/v1/camadas/${camadaId}/registros-diarios?fecha=${body.fecha}`,
        );
      if (currentOwner() !== ownerId)
        throw new Error('La sesión ha cambiado. Vuelve a guardar con tu usuario.');
      const projected = await enqueueParte(camadaId, body, baseline);
      if (navigator.onLine === false) return projected;
      await syncPendingParts().catch(() => {});
      const failure = await getParteSaveError(camadaId, body.fecha);
      if (failure) throw new Error(failure);
      const confirmed = await getSnapshot<ParteDelDiaPorCamada>(key, ownerId);
      return readProjectedParte(camadaId, body.fecha, confirmed ?? baseline);
    },
    // Tras guardar, invalidamos AMBAS queries que muestran datos del
    // parte: la del parte por camada y la del agregado de la Home
    // (que suma los partes de cada lote por separado).
    invalidateKeys: [
      ['liquidacion-evolucion'],
      [...qk('registros-diarios')],
      [...PARTES_CAMADA_KEY, camadaId, 'registros-diarios'],
    ],
  });
}
